import { Redline } from 'redline-client-sdk';
import { RLUserTrackingEvents } from '../types';
import { pixel } from './pixel';
import { appsFlyerBridge } from './appsFlyer';
import { controlEventSending, removeCtrlCookieOnEventError } from '../helpers/send-events-by-session';

declare global {
  interface Window {
    gtag: any;
  }
}

export const userTrackingEvents = (rl: Redline): RLUserTrackingEvents => ({
  consentAccepted: (payload = {}) => {
    const event = 'userTracking.ConsentAccepted.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  elementClicked: (payload = {}) => {
    const event = 'userTracking.ElementClicked.v1';
    const fmtPayload = { webElement: payload };
    rl.track(event, { event, ...fmtPayload });
    pixel(event, { event, ...fmtPayload });
  },
  elementRendered: (payload = {}, opts) => {
    const event = 'userTracking.ElementRendered.v1';
    const fmtPayload = { webElement: payload };
    const { debounceCookie, debounceID } = opts || {};
    const runEvt = () => {
      pixel(event, { event, ...fmtPayload });
      return rl.track(event, { event, ...fmtPayload });
    };

    if (debounceCookie && debounceID) {
      return controlEventSending(debounceCookie, debounceID, () => {
        return runEvt().catch((err) => {
          removeCtrlCookieOnEventError(debounceCookie, debounceID, err);
        });
      });
    }

    return runEvt();
  },
  elementViewed: (payload = {}, opts) => {
    const event = 'userTracking.ElementViewed.v1';
    const fmtPayload = { webElement: payload };
    const { debounceCookie, debounceID } = opts || {};
    const runEvt = () => {
      pixel(event, { event, ...fmtPayload });
      return rl.track(event, { event, ...fmtPayload });
    };

    if (debounceCookie && debounceID) {
      return controlEventSending(debounceCookie, debounceID, () => {
        return runEvt().catch((err) => {
          removeCtrlCookieOnEventError(debounceCookie, debounceID, err);
        });
      });
    }

    return runEvt();
  },
  formViewed: (payload = {}) => {
    const event = 'userTracking.FormViewed.v1'
    rl.track(event, { event, ...payload })
    pixel(event, { event, ...payload });
  },
  fieldInputted: (payload = {}) => {
    const event = 'userTracking.FieldInputted.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
  },
  fieldSelected: (payload = {}) => {
    const event = 'userTracking.FieldSelected.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
  },
  formOutcomeReceived: (payload = {}) => {
    const event = 'userTracking.FormOutcomeReceived.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  formStarted: (payload = {}) => {
    const event = 'userTracking.FormStarted.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  formSubmitted: (payload = {}) => {
    const event = 'userTracking.FormSubmitted.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  formContinued: (payload = {}) => {
    const event = 'userTracking.FormContinued.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  formBacked: (payload = {}) => {
    const event = 'userTracking.FormBacked.v1';
    rl.track(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  formErrored: (payload = {}) => {
    const event = 'userTracking.FormErrored.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  experimentStarted: (payload = {}) => {
    const event = 'userTracking.ExperimentStarted.v1';
    rl.track(event, { ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
  },
  adLoaded: (payload = {}) => {
    const event = 'userTracking.AdLoaded.v1';
    rl.track(event, { ...payload });
    if (window?.gtag) {
      window.gtag('event', 'admanager-ad-loaded', {
        ...payload,
      });
    }
  },
  adViewed: (payload = {}) => {
    const event = 'userTracking.AdViewed.v1';
    rl.track(event, { ...payload });
    if (window?.gtag) {
      window.gtag('event', 'admanager-ad-viewed', {
        ...payload,
      });
    }
  },
  adClicked: (payload = {}) => {
    const event = 'userTracking.AdClicked.v1';
    rl.track(event, { ...payload });
    if (window?.gtag) {
      window.gtag('event', 'admanager-ad-clicked', {
        ...payload,
      });
    }
  },
  scoreViewed: (payload = {}) => {
    const event = 'score.ScoreViewed.v1';
    rl.track(event, { event, ...payload });
    pixel(event, { event, ...payload });
  },
});
